import React, { FC } from 'react';
import { NavLink } from 'react-router-dom';
import { MutationFunctionOptions } from '@apollo/client';
import { AccountProfileFragment, ProfileStatusEnum } from '@minecraft.graphql-operations';
import { useTranslation } from '@blocs.i18n';
import { UpdatesFeed } from '@blocs.braze';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import SCREEN_NAMES from 'config/constants/screenNames';
import { StyledUpdateLink } from 'components/core/NavLoggedIn/Styles';
import { useRouteHelper } from 'utils/routeHelper';
import styled from 'styled-components';
import { FlexDynamic } from '@minecraft.atoms';
import { useArtistContext } from '../ArtistContext';

// We have to override styles because of react-strap
// Ideally we'd be able to use <BodyTextSmall and links in here
const StyledNavigationWrapper = styled(FlexDynamic)`
  & a,
  & a:not([href]):not([tabindex]) {
    display: block;
    font-size: 0.875rem;
  }
`;

interface MainNavMenuProps {
  profile: AccountProfileFragment;
  setUpdatedProfileField: (options: MutationFunctionOptions) => void;
  alertsCount: number;
}

const MainNavMenu: FC<MainNavMenuProps> = ({ profile, setUpdatedProfileField, alertsCount }) => {
  const { t } = useTranslation();
  const { link } = useRouteHelper();
  const { isArtistLocked } = useArtistContext();
  const submissionTrends = useFeatureExperiment(FEATURE_FLAGS.SUBMISSION_TRENDS);
  const yourAuditions = useFeatureExperiment(FEATURE_FLAGS.AUDITIONS);
  const yourInsights = submissionTrends || yourAuditions;

  return (
    <StyledNavigationWrapper
      direction="column"
      alignItems="flex-start"
      className="cn_mt-2"
      mdProps={{ direction: 'row', gap: '6', alignItems: 'center' }}
    >
      {profile.profileStatus.code === ProfileStatusEnum.Active && (
        <NavLink
          to={link('castingBillboard')}
          activeClassName="active"
          data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-casting-billboard`}
        >
          {t('common:mainNav.castingBillboard')}
        </NavLink>
      )}
      {!isArtistLocked && (
        <NavLink
          to={link('profile', profile.profileId)}
          activeClassName="active"
          data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-your-profile`}
          onClick={() =>
            setUpdatedProfileField({
              variables: { updatedProfileField: null },
            })
          }
        >
          {t('common:mainNav.yourProfile')}
        </NavLink>
      )}
      {!isArtistLocked && profile.profileStatus.code === ProfileStatusEnum.Active && (
        <div className="cn_relative">
          <NavLink
            to={link('mediaBank')}
            activeClassName="active"
            data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-media-bank`}
          >
            {t('common:mainNav.yourMedia')}
          </NavLink>
        </div>
      )}
      {profile.profileStatus.code === ProfileStatusEnum.Active && (
        <NavLink
          to={link('yourAlerts')}
          activeClassName="active"
          data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-your-alerts`}
        >
          {t('common:mainNav.yourAlerts', { count: alertsCount })}
        </NavLink>
      )}
      {!isArtistLocked && profile.profileStatus.code === ProfileStatusEnum.Active && (
        <>
          {yourInsights && (
            <NavLink
              to="/your-insights"
              activeClassName="active"
              data-qa-id={`${SCREEN_NAMES.NAVIGATION_LINK}-your-insights`}
            >
              {t('common:mainNav.yourInsights')}
            </NavLink>
          )}
          <div aria-label={t('common:mainNav.updates')}>
            <UpdatesFeed
              wrapperElement={StyledUpdateLink}
              additionalWrapperElProps={{
                'data-qa-id': `${SCREEN_NAMES.NAVIGATION_LINK}-braze-feed-updates`,
              }}
            />
          </div>
        </>
      )}
    </StyledNavigationWrapper>
  );
};

export default MainNavMenu;
